import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  BOSS_KEY_MAX, BOSS_KEY_MIN, bossKeyWarning, describeBossKey, getBossKey, keyLabel, keyToken,
  setBossKey, setBossKeyRecording, validateBossKey,
} from '../utils/bossKey';
import { bossToggle, fetchBossStatus, type BossStatus } from '../utils/desktopWindows';

/**
 * 设置页里的「老板键」面板：录制一串 2~3 个按键的组合，按下去就把整个应用收起来。
 *
 * 交互取舍：
 * - **手动"保存"而不是录满自动保存**：2 个键也是合法组合，录满才存的话用户没法表达
 *   "我就要这两个键"（双击 Ctrl 这种尤其容易被误判成"还想再按一个"）。
 * - **Enter 保存 / Esc 取消**：录制时这两个键不参与组合，避免想保存却录进去一个键。
 * - 录制中把 `preventDefault` 打满：否则按 Ctrl+F 会在编辑器里弹出查找面板之类的副作用。
 */
export function BossKeySetting(): React.ReactElement {
  const [saved, setSaved] = useState<string[] | null>(() => getBossKey());
  const [recording, setRecording] = useState(false);
  const [draft, setDraft] = useState<string[]>([]);
  const [hint, setHint] = useState<string | null>(null);
  const [status, setStatus] = useState<BossStatus | null>(null);
  const [probeResult, setProbeResult] = useState<{ pending: boolean; ok?: string | null; error?: string | null } | null>(null);

  // 录制回调里要读到"当前草稿"，但它不该把监听器重新挂一遍（每次按键都重挂很蠢）
  const draftRef = useRef<string[]>([]);
  draftRef.current = draft;

  const commit = useCallback((keys: string[]) => {
    const error = validateBossKey(keys);
    if (error) {
      setHint(error);
      return;
    }
    setBossKey(keys);
    setSaved(keys);
    setRecording(false);
    setDraft([]);
    setHint(null);
  }, []);

  // 录制期间告诉匹配器"这些键是在录、不是在用"：
  // 否则录「双击 Ctrl」的过程中就会命中并顺手把应用收起来（用户看不到自己在录什么）
  useEffect(() => {
    setBossKeyRecording(recording);
    return () => setBossKeyRecording(false);
  }, [recording]);

  // 录制：捕获阶段拦下按键
  useEffect(() => {
    if (!recording) return;
    const onKeyDown = (event: KeyboardEvent) => {
      // Alt+F4 不拦：录着的时候也该能直接关掉窗口（它是系统的关闭键，不是组合的一部分）
      if (event.altKey && event.key === 'F4') return;
      event.preventDefault();
      event.stopPropagation();
      if (event.key === 'Escape') {
        setRecording(false);
        setDraft([]);
        setHint(null);
        return;
      }
      if (event.key === 'Enter') {
        commit(draftRef.current);
        return;
      }
      if (event.repeat) return;
      const token = keyToken(event);
      if (!token) return;
      setDraft(prev => {
        if (prev.length >= BOSS_KEY_MAX) return prev;
        return [...prev, token];
      });
      setHint(null);
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [recording, commit]);

  const clear = () => {
    setBossKey(null);
    setSaved(null);
    setRecording(false);
    setDraft([]);
    setHint(null);
    setProbeResult(null);
  };

  // 运行状态：托盘与键盘钩子各有没有就绪。**这是排障用的** —— 老板键链路有三段
  // （页面下发组合 → 钩子匹配 → 原生收起），断在哪一段在这里一眼能看到。
  useEffect(() => {
    let alive = true;
    const load = async () => {
      const status = await fetchBossStatus();
      if (alive) setStatus(status);
    };
    void load();
    const timer = window.setInterval((): void => { void load(); }, 5000);
    return () => { alive = false; window.clearInterval(timer); };
  }, []);

  const probe = async (): Promise<void> => {
    setProbeResult({ pending: true });
    const result = await bossToggle();
    setProbeResult({
      pending: false,
      error: result.error,
      ok: result.hidden !== null ? (result.hidden ? '已收起（再用托盘图标或重新启动应用可恢复）' : '已恢复显示') : null,
    });
    setStatus(await fetchBossStatus());
  };

  const draftError = draft.length > 0 ? validateBossKey(draft) : null;
  // 已保存组合的"容易误触发"提醒（不阻止保存，只说实话）
  const savedWarning = saved && !recording ? bossKeyWarning(saved) : null;

  return (
    <div style={{ marginBottom: 20 }}>
      <h3 style={{ fontSize: 14, fontWeight: 600, margin: '0 0 8px' }}>老板键</h3>
      <p style={descStyle}>
        在任意页面按下这串按键，就能把<b>整个应用</b>收起来（主窗口与右侧吸附窗口一起隐藏）。
        <br />
        <b>收起后怎么回来</b>：① 点系统托盘的小天书图标（右键菜单里也有「显示主窗口」）；
        ② 直接重新启动一次小天书（已有实例会把窗口显示出来）；③ 托盘右键菜单可以「退出」。
      </p>
      <p style={{ ...descStyle, marginTop: -6 }}>
        ⚠️ 收起只是把窗口藏起来，<b>不会退出程序</b>；窗口右上角的 × 在有托盘时也是"收起到托盘"，要真正退出请用托盘右键菜单的「退出」。
      </p>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 13 }}>当前：</span>
        <code style={codeStyle}>{saved ? describeBossKey(saved) : '未设置'}</code>

        {!recording ? (
          <>
            <button type="button" onClick={() => { setRecording(true); setDraft([]); setHint(null); }} style={buttonStyle}>
              录制组合
            </button>
            {saved && (
              <button type="button" onClick={clear} style={buttonStyle}>清除</button>
            )}
            <button
              type="button"
              disabled={!saved || probeResult?.pending}
              onClick={() => void probe()}
              style={{ ...buttonStyle, opacity: !saved || probeResult?.pending ? 0.5 : 1, cursor: !saved ? 'not-allowed' : 'pointer' }}
            >
              试一下
            </button>
          </>
        ) : (
          <>
            <code style={{ ...codeStyle, minWidth: 72, textAlign: 'center', borderStyle: 'dashed', borderColor: 'var(--primary-color)', color: 'var(--primary-color)' }}>
              {draft.length > 0 ? draft.map(keyLabel).join(' + ') : '请按键…'}
            </code>
            <button
              type="button"
              disabled={Boolean(draftError)}
              onClick={() => commit(draft)}
              style={{ ...buttonStyle, opacity: draftError ? 0.5 : 1, cursor: draftError ? 'not-allowed' : 'pointer' }}
            >
              保存组合
            </button>
            <button type="button" onClick={() => { setRecording(false); setDraft([]); setHint(null); }} style={buttonStyle}>
              取消录制
            </button>
          </>
        )}
      </div>

      <p style={{
        ...descStyle,
        marginTop: 8,
        color: hint || draftError ? 'var(--danger-color)' : savedWarning ? '#d48806' : 'var(--text-secondary)',
      }}>
        {hint
          || draftError
          || savedWarning
          || (recording
            ? `按下 ${BOSS_KEY_MIN}~${BOSS_KEY_MAX} 个键（可以重复，例如双击 Ctrl、Ctrl+F+F），回车保存、Esc 取消`
            : '建议用不与编辑器现有快捷键冲突的组合（例如 Ctrl+Shift+B）；至少要有一个 Ctrl / Alt / Shift 键。')}
      </p>

      {/* 「试一下」的结果：失败时把服务端给的原因原样显示出来（这类失败以前是静默的） */}
      {probeResult && !probeResult.pending && (probeResult.error || probeResult.ok) && (
        <p style={{ ...descStyle, marginTop: -4, color: probeResult.error ? 'var(--danger-color)' : '#52c41a' }}>
          {probeResult.error ? `试一下失败：${probeResult.error}` : `试一下成功：${probeResult.ok}`}
        </p>
      )}

      {/* 运行状态：老板键链路三段（托盘 / 键盘钩子 / 组合）各自是否就绪 */}
      {status && (
        <p style={{ ...descStyle, marginTop: 4, fontSize: 12 }}>
          运行状态：
          <span style={{ color: status.trayOk ? '#52c41a' : '#d48806' }}>
            {status.trayOk ? '托盘已就绪' : '托盘不可用（收起会退化为"最小化"，任务栏可找回）'}
          </span>
          {' · '}
          <span style={{ color: status.hookOk ? '#52c41a' : 'var(--danger-color)' }}>
            {status.hookOk ? '键盘钩子已安装' : '键盘钩子未安装（快捷键不会触发）'}
          </span>
          {' · '}
          <span>已下发的组合：{status.keys.length > 0 ? describeBossKey(status.keys.map(String)) : '（空）'}</span>
          {' · '}
          {/* 钩子**实际看到**的按键：排障关键 —— 空 = 它一个都没收到（前台是管理员程序等）；
              非空但不命中 = 组合或键码不对。用户反馈问题时报这一行就够定位 */}
          <span style={{ color: status.lastKeys.length > 0 ? 'inherit' : 'var(--text-secondary)' }}>
            钩子最近看到：{status.lastKeys.length > 0 ? describeBossKey(status.lastKeys.map(String)) : '（还没收到任何按键）'}
          </span>
          {' · '}
          {/* 只增不减的计数：最近按键会被顶掉，用它判断"按键到底进没进钩子" */}
          <span>累计按键：{status.keyEvents}</span>
          {' · '}
          {/* 命中记录：区分"压根没命中"与"命中了但动作失败"（两者现象一样，原因完全不同） */}
          <span style={{ color: status.lastHit?.includes('失败') ? 'var(--danger-color)' : 'inherit' }}>
            上次命中：{status.lastHit ?? '（还没命中过）'}
          </span>
        </p>
      )}
    </div>
  );
}

const descStyle: React.CSSProperties = {
  fontSize: 12.5, color: 'var(--text-secondary)', margin: '0 0 12px', lineHeight: 1.7,
};

const codeStyle: React.CSSProperties = {
  fontSize: 13, padding: '3px 10px', borderRadius: 6,
  border: '1px solid var(--border-color)', background: 'var(--card-bg)',
};

const buttonStyle: React.CSSProperties = {
  padding: '5px 14px', border: '1px solid var(--border-color)', borderRadius: 6,
  cursor: 'pointer', fontSize: 13, background: 'transparent', color: 'var(--text-color)',
};
